import React, { ChangeEvent, FC, KeyboardEvent, useState } from 'react';
import { useIntl } from 'react-intl';

import { Input, ValidationMessage } from '@xsolla/ui-core';
import { Plus } from '@xsolla/ui-icons';

import { XSOLLA_ACCOUNT_DOCUMENTATION_LINKS } from '../../../../../constants';
import { DocumentationLink } from '../DocumentationLink';
import { InputWrapper, PlusButton, Wrapper } from './styled';

const SCOPE_FULL_REGEX = /^[a-z]+[a-z._]*[a-z]+$|^[a-z]$/;
const MAX_SCOPE_LENGTH = 255;

const getHintRenderer = (link: string) => {
  return link ? () => <DocumentationLink to={link} /> : undefined;
};

export const ScopeInput: FC<{
  dataTestId: string;
  scopes: string[];
  onAdd: (scope: string) => void;
  onCancel: () => void;
}> = ({ dataTestId, scopes, onAdd, onCancel }) => {
  const [inputValue, setInputValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const intl = useIntl();

  const validateScopeInput = (value: string): string | null => {
    if (!value) return null;

    if (value.length > MAX_SCOPE_LENGTH) {
      return intl.formatMessage({ id: 'app.validation.characters' }, { number: MAX_SCOPE_LENGTH });
    }

    if (!SCOPE_FULL_REGEX.test(value)) {
      return intl.formatMessage({
        id: 'authorization.settings.babkaLogin.field.scope.regex-error'
      });
    }

    return null;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const valueToSet = value.replace(/\s/g, '_');
    setInputValue(valueToSet);
    setValidationError(validateScopeInput(valueToSet));
  };

  const handleAdd = () => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue) {
      onCancel();
      return;
    }

    const error = validateScopeInput(trimmedValue);
    if (error) {
      setValidationError(error);
      return;
    }

    if (scopes.includes(trimmedValue)) {
      onCancel();
      return;
    }

    onAdd(trimmedValue);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Wrapper>
      <InputWrapper>
        <Input
          data-test-id={`${dataTestId}-input`}
          autoFocus
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={intl.formatMessage({
            id: 'authorization.settings.babkaLogin.modal.connect.scope.placeholder'
          })}
          extra={getHintRenderer(XSOLLA_ACCOUNT_DOCUMENTATION_LINKS.SCOPE)}
          extraAllowClick
          extraAlign="right"
        />
        <ValidationMessage>{validationError}</ValidationMessage>
      </InputWrapper>

      <PlusButton
        data-test-id={`${dataTestId}-apply`}
        type="button"
        onClick={handleAdd}
        palette="muted">
        <Plus />
      </PlusButton>
    </Wrapper>
  );
};
